import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Truck } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Progress } from "@/components/ui/progress";
import { useCart } from "@/context/CartContext";
import { eur, img } from "@/lib/api";

export const FreeShippingBar = () => {
  const { subtotal, settings } = useCart();
  const left = Math.max(0, settings.free_shipping_from - subtotal);
  const pct = Math.min(100, (subtotal / settings.free_shipping_from) * 100);
  return (
    <div className="bg-[#EFF6FF] p-4 rounded" data-testid="free-shipping-bar">
      <p className="text-sm text-[#0B1D3A] flex items-center gap-2 font-semibold">
        <Truck size={16} className="text-[#2563EB]" />
        {left > 0 ? <>Te faltan <span className="text-[#DC2626]">{eur(left)}</span> para el envío gratis</> : "¡Tienes envío gratis!"}
      </p>
      <Progress value={pct} className="h-2 mt-3 bg-white [&>div]:bg-[#DC2626]" />
    </div>
  );
};

const CartLine = ({ it }) => {
  const { setQty, remove } = useCart();
  const id = `${it.slug}-${it.format_id}`;
  return (
    <div className="flex gap-3 py-4 border-b border-slate-100" data-testid={`cart-item-${id}`}>
      <img src={img(it.image)} alt={it.name} className="w-20 h-20 object-contain bg-slate-50 rounded" />
      <div className="flex-1 min-w-0">
        <p className="font-display font-bold text-[#0B1D3A] leading-tight">{it.name}</p>
        <p className="text-xs text-slate-500 mt-0.5">{it.format_label}</p>
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center border border-slate-200 rounded-full">
            <button className="p-1.5" onClick={() => setQty(it.slug, it.format_id, it.qty - 1)} data-testid={`cart-dec-${id}`}><Minus size={14} /></button>
            <span className="w-7 text-center text-sm font-semibold" data-testid={`cart-qty-${id}`}>{it.qty}</span>
            <button className="p-1.5" onClick={() => setQty(it.slug, it.format_id, it.qty + 1)} data-testid={`cart-inc-${id}`}><Plus size={14} /></button>
          </div>
          <span className="font-bold text-[#DC2626]">{eur(it.price * it.qty)}</span>
        </div>
      </div>
      <button onClick={() => remove(it.slug, it.format_id)} className="self-start p-1 text-slate-400 hover:text-[#DC2626]" data-testid={`cart-remove-${id}`} aria-label="Eliminar">
        <Trash2 size={16} />
      </button>
    </div>
  );
};

export const DiscountLines = () => {
  const { discount, discountPct, nextTier, subtotal } = useCart();
  return (
    <>
      {discount > 0 && <div className="flex justify-between text-sm text-[#DC2626] font-semibold"><span>Descuento por volumen ({discountPct} %)</span><span data-testid="cart-discount">-{eur(discount)}</span></div>}
      {nextTier && subtotal > 0 && (
        <p className="text-xs bg-[#EFF6FF] text-[#1E3A8A] rounded px-3 py-2" data-testid="next-tier-hint">
          Añade <b className="text-[#DC2626]">{eur(nextTier.min - subtotal)}</b> más y consigue un <b>{nextTier.pct} % de descuento</b>
        </p>
      )}
    </>
  );
};

export const CartDrawer = () => {
  const { items, open, setOpen, subtotal, shipping, total } = useCart();
  const nav = useNavigate();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col p-0" data-testid="cart-drawer">
        <SheetHeader className="p-5 border-b">
          <SheetTitle className="font-display text-2xl text-[#0B1D3A]">Tu carrito</SheetTitle>
        </SheetHeader>
        <div className="p-5 flex-1 overflow-y-auto">
          <FreeShippingBar />
          {items.length === 0 ? (
            <div className="text-center py-16" data-testid="cart-empty">
              <p className="text-slate-500">Tu carrito está vacío.</p>
              <Link to="/tienda" onClick={() => setOpen(false)} className="inline-block mt-4 font-display font-bold text-[#DC2626]">Ver productos →</Link>
            </div>
          ) : items.map((it) => <CartLine key={`${it.slug}-${it.format_id}`} it={it} />)}
        </div>
        {items.length > 0 && (
          <div className="border-t p-5 space-y-2 bg-white">
            <div className="flex justify-between text-sm"><span>Subtotal</span><span data-testid="cart-subtotal">{eur(subtotal)}</span></div>
            <DiscountLines />
            <div className="flex justify-between text-sm"><span>Envío</span><span data-testid="cart-shipping">{shipping ? eur(shipping) : "Gratis"}</span></div>
            <div className="flex justify-between font-bold text-lg pt-1"><span>Total <span className="text-xs font-normal text-slate-500">IVA incl.</span></span><span className="text-[#DC2626]" data-testid="cart-total">{eur(total)}</span></div>
            <button onClick={() => { setOpen(false); nav("/checkout"); }} data-testid="cart-checkout-button"
              className="w-full mt-3 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-display font-bold text-lg py-3.5 rounded-full transition-colors">
              Finalizar compra
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
